import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';
import type { EngagementMetricsQueryParams } from '../types/engagement-metrics.types';

const DEBOUNCE_MS = 300;

export const engagementMetricsLogKeys = {
  all: ['analytics', 'engagement-metrics', 'log'] as const,
  list: (params?: Omit<EngagementMetricsQueryParams, 'cursor'>) =>
    [...engagementMetricsLogKeys.all, 'list', params] as const,
};

/** Quick-filter chip values from the design spec: All / Billable / Non-billable. */
export type BillableQuickFilter = 'ALL' | 'BILLABLE' | 'NON_BILLABLE';

export const useEngagementMetricsLogData = (
  baseParams: EngagementMetricsQueryParams,
  enabled = true,
) => {
  const [search, setSearchRaw] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [billableFilter, setBillableFilter] = useState<BillableQuickFilter>('ALL');
  const [reportingType, setReportingType] = useState('');
  const [initiatorRole, setInitiatorRole] = useState('');

  const setSearch = useCallback((value: string) => {
    setSearchRaw(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => setDebouncedSearch(value), DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const queryParams = useMemo<Omit<EngagementMetricsQueryParams, 'cursor'>>(() => {
    const params: Omit<EngagementMetricsQueryParams, 'cursor'> = { ...baseParams, limit: 50 };
    if (debouncedSearch) params.search = debouncedSearch;
    if (billableFilter === 'BILLABLE') params.isBillable = true;
    if (billableFilter === 'NON_BILLABLE') params.isBillable = false;
    if (reportingType) params.reportingType = reportingType;
    if (initiatorRole) params.initiatorRole = initiatorRole;
    return params;
  }, [baseParams, debouncedSearch, billableFilter, reportingType, initiatorRole]);

  const query = useInfiniteQuery({
    queryKey: engagementMetricsLogKeys.list(queryParams),
    queryFn: async ({ pageParam }) => {
      const response = await analyticsApi.getEngagementMetricsLog({
        ...queryParams,
        cursor: pageParam ?? undefined,
      });
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data?.pages],
  );

  return {
    items,
    exportParams: queryParams,
    search,
    setSearch,
    billableFilter,
    setBillableFilter,
    reportingType,
    setReportingType,
    initiatorRole,
    setInitiatorRole,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
